/**
 * MED-1319 Public booking page at /book/:token.
 *
 * A participant opens a link from their coach and books a 1:1 session without
 * a login. The page is outside ProtectedRoute and PublicRoute, so a logged-in
 * coach or participant can also open it.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Box,
  Button,
  Group,
  Image,
  Loader,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  AlertCircle,
  ArrowLeft,
  Calendar as CalendarIcon,
  Check,
  Clock,
  Link2,
  Monitor,
  User,
} from 'react-feather';
import {
  AVAILABILITY_FAILED_MESSAGE,
  AvailabilityFailureAlert,
} from '../../components/scheduling/AvailabilityFailureAlert';
import { SlotPicker } from '../../components/scheduling/SlotPicker';
import { useMonthAvailability } from '../../components/scheduling/useMonthAvailability';
import {
  ChangeOrCancel,
  InviteOnItsWay,
} from '../../components/scheduling/InviteSentNotice';
import {
  MONTH_NAMES,
  formatMonthKey,
  getTimezoneShortName,
} from '../../components/scheduling/slotPickerUtils';
import {
  publicBookingApi,
  isPublicBookingError,
  type PublicBookingLink,
  type PublicBookingResult,
  type PublicBookingSlot,
} from '../../services/api/publicBooking';
import { validateEmail } from '../../utils/validation';
import { downloadBookingIcs } from './bookingIcs';
import { schedulingTokens as tokens } from '../../components/scheduling/schedulingTokens';
import classes from './PublicBookingPage.module.css';
import medleyLogoWhite from '../../assets/medley-logo-white.png';

// ============================================================================
// Types
// ============================================================================

type Phase =
  | { kind: 'loading' }
  | { kind: 'not_found' }
  | { kind: 'load_error'; message: string }
  | { kind: 'ready'; link: PublicBookingLink }
  | {
      kind: 'done';
      link: PublicBookingLink;
      result: PublicBookingResult;
      email: string;
    };

type Step = 'pick' | 'details';

const NAME_MAX_LENGTH = 200;

const LOGIN_PATH = '/participant/login';

const NETWORK_MESSAGE =
  'We could not reach Medley. Check your connection and try again.';
const MONTH_COUNT_WORDS: Record<number, string> = { 2: 'two', 3: 'three' };
const RATE_LIMIT_MESSAGE =
  'Too many attempts in a short time. Wait a minute, then try again.';
const SERVER_MESSAGE = 'Something went wrong on our side. Please try again.';
const SLOT_TAKEN_MESSAGE =
  'Someone booked that time a moment ago. Pick another time.';
const BOOKING_FAILED_MESSAGE = 'We could not book this time. Please try again.';
const BOOKING_BLOCKED_MESSAGE =
  'Online booking is not available for this coach right now. Contact the Medley team at team@withmedley.com to book this session.';

// ============================================================================
// Helpers
// ============================================================================

/** Return the coach's full name, or null when the group has no coach. */
/**
 * The API label of a 1:1 link ends with its length, as in "1:1 session
 * (45 min)". Each screen shows the length on its own row, so remove it here.
 */
function sessionTitle(touchpointLabel: string): string {
  return touchpointLabel.replace(/\s*\(\d+ min\)$/, '');
}

function getCoachName(link: PublicBookingLink): string | null {
  const name = `${link.coach.first_name} ${link.coach.last_name}`.trim();
  return name || null;
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function formatFullDate(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });
}

function formatTime(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

/** Return "2:30 PM to 3:00 PM MDT". */
function formatTimeRange(
  isoStart: string,
  durationMinutes: number,
  tz: string
): string {
  const end = new Date(
    new Date(isoStart).getTime() + durationMinutes * 60_000
  ).toISOString();
  return `${formatTime(isoStart, tz)} to ${formatTime(end, tz)} ${getTimezoneShortName(tz, new Date(isoStart))}`;
}

// MED-1389 (QA round 3): the confirmation is no longer kept for a refresh.
// Staff can cancel the booking after it was made, so a kept confirmation can
// be false. Delete the copy that earlier builds stored: it holds the booker's
// email, and the next person on a shared device must not see it.
function forgetStoredBooking(token: string) {
  try {
    sessionStorage.removeItem(`medley-booking:${token}`);
  } catch {
    // Storage can be off, for example in a private window.
  }
}

// ============================================================================
// Small parts
// ============================================================================

function PageShell({ children }: { children: ReactNode }) {
  return (
    <Box className={classes.wrapper}>
      <div className={classes.pattern} />
      <main className={classes.page}>
        <Box className={classes.logo}>
          <Image
            src={medleyLogoWhite}
            alt="Medley"
            h={24}
            w="auto"
            fit="contain"
          />
        </Box>
        {children}
      </main>
    </Box>
  );
}

interface MessageCardProps {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  headingRef?: React.Ref<HTMLHeadingElement>;
}

function MessageCard({ icon, title, children, headingRef }: MessageCardProps) {
  return (
    <Paper
      className={`${classes.card} ${classes.cardNarrow}`}
      p={{ base: 'lg', xs: 40 }}
      radius="lg"
      shadow="xl"
    >
      <Stack align="center" gap="md">
        {icon}
        <Title
          order={1}
          size="h3"
          ta="center"
          ref={headingRef}
          tabIndex={-1}
          className={classes.heading}
        >
          {title}
        </Title>
        {children}
      </Stack>
    </Paper>
  );
}

function DetailRow({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <Box
        mt={3}
        style={{ display: 'flex', flexShrink: 0, color: tokens.textTertiary }}
        aria-hidden
      >
        {icon}
      </Box>
      <Text size="sm" c="dimmed">
        {children}
      </Text>
    </Group>
  );
}

// MED-1365 QA round 3: no timezone line here. The slot picker names the
// zone for the month on screen, and each chosen time carries its
// abbreviation, so the zone shows once on each screen.
function CoachInfo({ link }: { link: PublicBookingLink }) {
  // A group with no coach shows no avatar and no name. The page text says
  // that the group does not have a coach yet.
  const coachName = getCoachName(link);
  return (
    <Stack gap="md">
      <Box>
        {coachName && (
          <>
            <Avatar
              src={link.coach.photo_url}
              alt={coachName}
              size={56}
              radius="xl"
              color="medleyGreen"
              variant="filled"
              mb="sm"
            >
              {getInitials(link.coach.first_name, link.coach.last_name)}
            </Avatar>
            <Text size="sm" c="dimmed" mb={2}>
              {coachName}
            </Text>
          </>
        )}
        <Title order={1} size="h3" fw={700}>
          {sessionTitle(link.touchpoint_label)}
        </Title>
      </Box>
      <Stack gap={8}>
        <DetailRow icon={<Clock size={14} />}>
          {link.duration_minutes} min
        </DetailRow>
        <DetailRow icon={<Monitor size={14} />}>Video meeting</DetailRow>
      </Stack>
      <Box className={classes.chip}>
        <Text size="xs" c="dimmed">
          {link.group_name} &middot; {link.cohort_name}
        </Text>
      </Box>
    </Stack>
  );
}

// ============================================================================
// Page
// ============================================================================

export function PublicBookingPage() {
  const { token = '' } = useParams<{ token: string }>();
  const timezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    []
  );
  const stacked = useMediaQuery('(max-width: 62em)', true, {
    getInitialValueInEffect: false,
  });

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [step, setStep] = useState<Step>('pick');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<PublicBookingSlot | null>(
    null
  );
  // The notice on the picker after a slot_unavailable error, else null.
  const [slotNotice, setSlotNotice] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  // A booking error that keeps the viewer on the details step.
  const [submitNotice, setSubmitNotice] = useState<string | null>(null);
  // True after booking_failed: Calendly can hold the booking already, so a
  // retry must not look like the normal next step.
  const [bookingUncertain, setBookingUncertain] = useState(false);
  // True after booking_blocked (MED-1363): a retry of the same slot cannot
  // succeed, so Book session stays off until the viewer picks another slot.
  const [bookingBlocked, setBookingBlocked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const headingRef = useRef<HTMLHeadingElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const slotNoticeRef = useRef<HTMLDivElement>(null);

  const link =
    phase.kind === 'ready' || phase.kind === 'done' ? phase.link : null;

  // Load the booking link.
  useEffect(() => {
    let cancelled = false;
    forgetStoredBooking(token);
    setPhase({ kind: 'loading' });
    publicBookingApi
      .getBookingLink(token)
      .then(data => {
        if (!cancelled) setPhase({ kind: 'ready', link: data });
      })
      .catch(err => {
        if (cancelled) return;
        if (isPublicBookingError(err) && err.code === 'link_not_found') {
          setPhase({ kind: 'not_found' });
        } else if (isPublicBookingError(err) && err.code === 'rate_limited') {
          setPhase({ kind: 'load_error', message: RATE_LIMIT_MESSAGE });
        } else if (isPublicBookingError(err) && err.code === 'network_error') {
          setPhase({ kind: 'load_error', message: NETWORK_MESSAGE });
        } else {
          setPhase({ kind: 'load_error', message: SERVER_MESSAGE });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, loadAttempt]);

  // Give the app title back when the viewer leaves the page. This effect runs
  // first, so it saves the app title before the next effect changes it.
  useEffect(() => {
    const previous = document.title;
    return () => {
      document.title = previous;
    };
  }, []);

  // Name the browser tab on every screen. The app default is an admin title,
  // which a visitor must never see.
  const pageTitle = (() => {
    if (phase.kind === 'not_found') return 'Booking link not found | Medley';
    if (phase.kind === 'done') return "You're booked | Medley";
    if (!link) return 'Booking | Medley';
    const coachFirstName = link.coach.first_name.trim();
    return coachFirstName
      ? `Book your ${sessionTitle(link.touchpoint_label)} with ${coachFirstName} | Medley`
      : `Book your ${sessionTitle(link.touchpoint_label)} | Medley`;
  })();
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const fetchWindow = useCallback(
    (start: string, end: string) =>
      publicBookingApi.getBookingAvailability(token, start, end),
    [token]
  );

  const availability = useMonthAvailability<PublicBookingSlot>({
    enabled: phase.kind === 'ready' && phase.link.available,
    sourceKey: token,
    timezone,
    fetchWindow,
    timeoutMs: 15000,
    // Skip up to two empty months, so that late in a month the first view
    // is not a month with no times.
    autoAdvanceMonths: 2,
  });

  // An availability error can show that the link changed state after the page loaded.
  const failure = availability.failure;
  useEffect(() => {
    // The guard on `available` stops a loop, because this effect replaces `phase`.
    if (
      !isPublicBookingError(failure) ||
      phase.kind !== 'ready' ||
      !phase.link.available
    )
      return;
    if (failure.code === 'link_not_found') {
      setPhase({ kind: 'not_found' });
    } else if (failure.code === 'booking_unavailable') {
      setPhase({ kind: 'ready', link: { ...phase.link, available: false } });
    }
  }, [failure, phase]);

  // Move focus to the heading of each new screen, so screen readers announce it.
  // The first picker screen keeps the page focus. The first screen of a link
  // that cannot book gets the focus, because the heading is its only content.
  const isFirstScreen = useRef(true);
  const linkAvailable = phase.kind === 'ready' ? phase.link.available : null;
  useEffect(() => {
    if (phase.kind === 'loading') return;
    if (isFirstScreen.current) {
      isFirstScreen.current = false;
      if (phase.kind === 'ready' && step === 'pick' && linkAvailable) return;
    }
    if (phase.kind === 'ready' && step === 'details' && linkAvailable) {
      nameRef.current?.focus();
    } else {
      headingRef.current?.focus();
    }
  }, [phase.kind, step, linkAvailable]);

  // After slot_unavailable, move focus to the notice that says why the viewer
  // is back on the picker. This effect runs after the heading effect.
  useEffect(() => {
    if (slotNotice && step === 'pick') slotNoticeRef.current?.focus();
  }, [slotNotice, step]);

  // Leave the confirmation and go back to the picker for a new booking.
  const handleBookAnother = () => {
    if (phase.kind !== 'done') return;
    // The next booker may be somebody else on the same device.
    setName('');
    setEmail('');
    setSelectedDate(null);
    setSelectedSlot(null);
    setSubmitNotice(null);
    setBookingUncertain(false);
    setBookingBlocked(false);
    setStep('pick');
    setPhase({ kind: 'ready', link: phase.link });
    availability.reload();
  };

  const handleSelectDate = (dateKey: string) => {
    setSelectedDate(dateKey);
    setSelectedSlot(null);
  };

  const handleSelectSlot = (slot: PublicBookingSlot) => {
    setSelectedSlot(slot);
    setSlotNotice(null);
    setBookingUncertain(false);
    setBookingBlocked(false);
  };

  const handleNext = () => {
    if (!selectedSlot) return;
    setSubmitNotice(null);
    setNameError(null);
    setEmailError(null);
    setStep('details');
  };

  const handleBack = () => {
    setSubmitNotice(null);
    setNameError(null);
    setEmailError(null);
    setStep('pick');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !selectedSlot ||
      phase.kind !== 'ready' ||
      isSubmitting ||
      bookingBlocked
    )
      return;

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const nameInvalid = !trimmedName
      ? 'Enter your name.'
      : trimmedName.length > NAME_MAX_LENGTH
        ? `Use ${NAME_MAX_LENGTH} characters or fewer.`
        : null;
    const emailInvalid = validateEmail(trimmedEmail);
    setNameError(nameInvalid);
    setEmailError(
      emailInvalid
        ? emailInvalid === 'Invalid email format'
          ? 'Enter a valid email address.'
          : 'Enter your email address.'
        : null
    );
    if (nameInvalid || emailInvalid) {
      (nameInvalid ? nameRef : emailRef).current?.focus();
      return;
    }

    setSubmitNotice(null);
    setIsSubmitting(true);
    try {
      const result = await publicBookingApi.bookPublicSession(token, {
        name: trimmedName,
        email: trimmedEmail,
        scheduled_start: selectedSlot.start_time,
        timezone,
      });
      setPhase({ kind: 'done', link: phase.link, result, email: trimmedEmail });
    } catch (err) {
      const code = isPublicBookingError(err) ? err.code : 'unknown';
      const message = isPublicBookingError(err)
        ? err.message
        : BOOKING_FAILED_MESSAGE;
      switch (code) {
        case 'slot_unavailable':
          // The server sentence says if the time is gone, or if another
          // booking is in progress and the viewer must wait.
          setSlotNotice(
            (isPublicBookingError(err) && err.serverMessage) ||
              SLOT_TAKEN_MESSAGE
          );
          setSelectedSlot(null);
          setSelectedDate(null);
          setStep('pick');
          availability.reload();
          break;
        case 'booking_unavailable':
          setPhase({
            kind: 'ready',
            link: { ...phase.link, available: false },
          });
          break;
        case 'link_not_found':
          setPhase({ kind: 'not_found' });
          break;
        case 'rate_limited':
          setSubmitNotice(RATE_LIMIT_MESSAGE);
          break;
        case 'network_error':
          setSubmitNotice(NETWORK_MESSAGE);
          break;
        case 'validation_error':
          setSubmitNotice(message);
          break;
        case 'booking_blocked':
          // MED-1363: the coach's Calendly refused the booking (plan, daily
          // limit or a required question). No booking exists, and a retry
          // soon does not help. The server's sentence says what to do. Clear
          // the retry state, so the button does not offer a retry.
          setBookingUncertain(false);
          setBookingBlocked(true);
          setSubmitNotice(
            (isPublicBookingError(err) && err.serverMessage) ||
              BOOKING_BLOCKED_MESSAGE
          );
          break;
        case 'booking_failed':
          // Calendly can hold the booking even when the API cannot confirm it.
          // The server's sentence then tells the viewer to check for an invite
          // before a retry, so show it and stay on the form.
          setBookingUncertain(true);
          setSubmitNotice(
            (isPublicBookingError(err) && err.serverMessage) ||
              BOOKING_FAILED_MESSAGE
          );
          break;
        default:
          // internal_error and any other error: ask for a retry.
          setSubmitNotice(BOOKING_FAILED_MESSAGE);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ==========================================================================
  // Render: whole-page states
  // ==========================================================================

  if (phase.kind === 'loading') {
    return (
      <PageShell>
        <Paper
          className={`${classes.card} ${classes.cardNarrow}`}
          p={40}
          radius="lg"
          shadow="xl"
        >
          <Stack align="center" gap="md" role="status">
            <Loader size="lg" color="medleyGreen" />
            <Text c="dimmed">Loading booking details...</Text>
          </Stack>
        </Paper>
      </PageShell>
    );
  }

  if (phase.kind === 'not_found') {
    return (
      <PageShell>
        <MessageCard
          icon={
            <Link2
              size={40}
              style={{ color: tokens.textTertiary }}
              aria-hidden
            />
          }
          title="This booking link does not work"
          headingRef={headingRef}
        >
          <Text c="dimmed" ta="center">
            {/* MED-1353: staff can also regenerate a link, and a closed
                cohort or an inactive group turns it off too. */}
            The link is not complete, was replaced with a new one, or is no
            longer open. Ask your coach for the current link.
          </Text>
          <Button
            component={Link}
            to={LOGIN_PATH}
            variant="light"
            color="medleyGreen"
            mt="sm"
          >
            Log in to Medley
          </Button>
        </MessageCard>
      </PageShell>
    );
  }

  if (phase.kind === 'load_error') {
    return (
      <PageShell>
        <MessageCard
          icon={
            <AlertCircle
              size={40}
              style={{ color: tokens.danger }}
              aria-hidden
            />
          }
          title="We could not load this page"
          headingRef={headingRef}
        >
          <Text c="dimmed" ta="center">
            {phase.message}
          </Text>
          <Button
            color="medleyGreen"
            mt="sm"
            onClick={() => setLoadAttempt(n => n + 1)}
          >
            Try again
          </Button>
        </MessageCard>
      </PageShell>
    );
  }

  if (phase.kind === 'done') {
    const { result } = phase;
    return (
      <PageShell>
        <MessageCard
          icon={
            <Box className={classes.successIcon} aria-hidden>
              <Check size={28} />
            </Box>
          }
          title="You're booked"
          headingRef={headingRef}
        >
          <Box className={classes.summary} w="100%">
            <Text fw={600} size="sm" mb="xs">
              {sessionTitle(result.touchpoint_label)}
            </Text>
            <Stack gap={6}>
              <DetailRow icon={<CalendarIcon size={14} />}>
                {formatFullDate(result.scheduled_start, timezone)}
              </DetailRow>
              <DetailRow icon={<Clock size={14} />}>
                <Text span fw={600} c="dark">
                  {formatTimeRange(
                    result.scheduled_start,
                    result.duration_minutes,
                    timezone
                  )}
                </Text>
              </DetailRow>
              <DetailRow icon={<User size={14} />}>
                {result.coach_name}
              </DetailRow>
              <DetailRow icon={<Monitor size={14} />}>
                {result.video_link ? (
                  <Anchor
                    href={result.video_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                  >
                    Join the video meeting
                  </Anchor>
                ) : (
                  'The video link is in your calendar invite'
                )}
              </DetailRow>
            </Stack>
          </Box>
          {/* MED-1389: no address. A typed address that matches a member
              (MDF-259) gets the invite at the roster address, and the page
              must not show which one, or whether the email matched. */}
          <InviteOnItsWay fallback={!!result.calendly_fallback} />
          {/* MED-1365: a fallback booking already sent a Google invite. */}
          {!result.calendly_fallback && (
            <Button
              variant="default"
              leftSection={<CalendarIcon size={16} />}
              onClick={() =>
                downloadBookingIcs({
                  scheduledStart: result.scheduled_start,
                  durationMinutes: result.duration_minutes,
                  title: sessionTitle(result.touchpoint_label),
                  coachName: result.coach_name,
                  videoLink: result.video_link,
                })
              }
            >
              Add to calendar
            </Button>
          )}
          <ChangeOrCancel
            fallback={!!result.calendly_fallback}
            surface="public"
            contactEmail={result.contact_email || undefined}
          />
          <Anchor
            component="button"
            type="button"
            size="sm"
            c="medleyGreen.8"
            onClick={handleBookAnother}
          >
            Book another time
          </Anchor>
        </MessageCard>
      </PageShell>
    );
  }

  // ==========================================================================
  // Render: booking unavailable
  // ==========================================================================

  const readyLink = phase.link;
  const readyCoachName = getCoachName(readyLink);
  const coachName = readyCoachName ?? 'your coach';

  if (!readyLink.available) {
    return (
      <PageShell>
        <Paper
          className={`${classes.card} ${classes.cardNarrow}`}
          radius="lg"
          shadow="xl"
        >
          <Box className={classes.infoStacked}>
            <CoachInfo link={readyLink} />
          </Box>
          <Stack gap="sm" p="lg">
            <Title
              order={2}
              size="h4"
              ref={headingRef}
              tabIndex={-1}
              className={classes.heading}
            >
              Booking is not open yet
            </Title>
            <Text size="sm" c="dimmed">
              {readyCoachName
                ? `${readyLink.coach.first_name} has not finished setting up online booking. Contact your coach, or try this link again later.`
                : 'This group does not have a coach yet. Try this link again later.'}
            </Text>
          </Stack>
        </Paper>
      </PageShell>
    );
  }

  // ==========================================================================
  // Render: details step
  // ==========================================================================

  const submitNoticeAlert = submitNotice && (
    <Alert
      color="warningAmber.5"
      variant="light"
      icon={<AlertCircle size={16} />}
      styles={{ message: { color: tokens.textPrimary } }}
    >
      <Text size="sm">{submitNotice}</Text>
    </Alert>
  );

  if (step === 'details' && selectedSlot) {
    return (
      <PageShell>
        <Paper
          className={`${classes.card} ${classes.cardNarrow}`}
          radius="lg"
          shadow="xl"
        >
          <Group
            gap="xs"
            px="lg"
            py="md"
            wrap="nowrap"
            style={{ borderBottom: `1px solid ${tokens.border}` }}
          >
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleBack}
              disabled={isSubmitting}
              size="lg"
              aria-label="Back to times"
            >
              <ArrowLeft size={16} />
            </ActionIcon>
            <Title
              order={1}
              size="h4"
              ref={headingRef}
              tabIndex={-1}
              className={classes.heading}
            >
              Confirm your session
            </Title>
          </Group>

          <form onSubmit={handleSubmit} noValidate>
            <Stack gap="md" p="lg">
              <Box className={classes.summary}>
                <Text fw={600} size="sm" mb="xs">
                  {sessionTitle(readyLink.touchpoint_label)}
                </Text>
                <Stack gap={6}>
                  <DetailRow icon={<CalendarIcon size={14} />}>
                    {formatFullDate(selectedSlot.start_time, timezone)}
                  </DetailRow>
                  <DetailRow icon={<Clock size={14} />}>
                    <Text span fw={600} c="dark">
                      {formatTimeRange(
                        selectedSlot.start_time,
                        readyLink.duration_minutes,
                        timezone
                      )}
                    </Text>
                  </DetailRow>
                  <DetailRow icon={<User size={14} />}>
                    {readyCoachName ?? 'Your coach'}
                  </DetailRow>
                  <DetailRow icon={<Monitor size={14} />}>
                    Video meeting link included
                  </DetailRow>
                </Stack>
              </Box>

              {submitNotice && bookingUncertain && submitNoticeAlert}

              <TextInput
                ref={nameRef}
                label="Your name"
                autoComplete="name"
                required
                maxLength={NAME_MAX_LENGTH}
                value={name}
                onChange={e => {
                  setName(e.currentTarget.value);
                  if (nameError) setNameError(null);
                }}
                error={nameError}
                readOnly={isSubmitting}
                size="md"
              />

              <TextInput
                ref={emailRef}
                label="Your email"
                description="Use the email address your program invitations go to, so your coach can match the session to your account."
                type="email"
                autoComplete="email"
                inputMode="email"
                required
                value={email}
                onChange={e => {
                  setEmail(e.currentTarget.value);
                  if (emailError) setEmailError(null);
                }}
                error={emailError}
                readOnly={isSubmitting}
                size="md"
              />

              {submitNotice && !bookingUncertain && submitNoticeAlert}

              {bookingUncertain ? (
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  variant="outline"
                  color="gray"
                  loading={isSubmitting}
                >
                  I did not get an invite - try again
                </Button>
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  loading={isSubmitting}
                  disabled={bookingBlocked}
                  aria-label="Book session"
                  style={
                    bookingBlocked
                      ? undefined
                      : { backgroundColor: tokens.primary, border: 'none' }
                  }
                >
                  Book session
                </Button>
              )}

              <Text size="xs" c="dimmed" ta="center">
                We will send a calendar invite to you and {coachName}.
              </Text>
            </Stack>
          </form>
        </Paper>
      </PageShell>
    );
  }

  // ==========================================================================
  // Render: pick a date and time
  // ==========================================================================

  const visibleMonthKey = formatMonthKey(
    availability.viewYear,
    availability.viewMonth
  );
  const monthHasSlots = [...availability.slotsByDate.keys()].some(key =>
    key.startsWith(visibleMonthKey)
  );
  const showEmpty =
    !availability.isLoading &&
    !failure &&
    availability.loadedMonthKey === visibleMonthKey &&
    !monthHasSlots;
  const failureCode = isPublicBookingError(failure) ? failure.code : null;
  const failureMessage =
    failureCode === 'rate_limited'
      ? RATE_LIMIT_MESSAGE
      : failureCode === 'network_error'
        ? 'We could not load open times. Check your connection and try again.'
        : AVAILABILITY_FAILED_MESSAGE;

  // No window loaded. Explain the dead grid above it, where the viewer looks.
  const pickerTopStatus = !availability.isLoading && failure != null && (
    <AvailabilityFailureAlert
      message={failureMessage}
      onRetry={availability.reload}
    />
  );

  // After the page skipped empty months on its own, say that all of them
  // were empty, not only the month on screen.
  const emptyMonthsLabel =
    availability.emptyMonthsSearched > 1
      ? `the next ${MONTH_COUNT_WORDS[availability.emptyMonthsSearched] ?? availability.emptyMonthsSearched} months`
      : null;

  const pickerStatus = (
    <>
      {showEmpty && (
        <Stack align="center" gap="xs" mt="md">
          <Text size="sm" c="dimmed" ta="center">
            {readyCoachName ?? 'Your coach'} has no open times in{' '}
            {emptyMonthsLabel ?? MONTH_NAMES[availability.viewMonth]}.
          </Text>
          <Button
            size="xs"
            variant="light"
            color="medleyGreen"
            onClick={availability.nextMonth}
          >
            Check next month
          </Button>
        </Stack>
      )}
    </>
  );

  const slotTakenAlert = slotNotice && (
    <Alert
      ref={slotNoticeRef}
      tabIndex={-1}
      color="warningAmber.5"
      variant="light"
      icon={<AlertCircle size={16} />}
      mb="md"
    >
      {slotNotice}
    </Alert>
  );

  const picker = (
    <SlotPicker
      availability={availability}
      timezone={timezone}
      selectedDate={selectedDate}
      onSelectDate={handleSelectDate}
      onClearSelection={() => {
        setSelectedDate(null);
        setSelectedSlot(null);
      }}
      selectedSlot={selectedSlot}
      onSelectSlot={handleSelectSlot}
      onNext={handleNext}
      status={pickerStatus}
      topStatus={pickerTopStatus}
      layout={stacked ? 'stacked' : 'panels'}
    />
  );

  return (
    <PageShell>
      <Paper
        className={classes.card}
        radius="lg"
        shadow="xl"
        maw={stacked ? 560 : 960}
      >
        {stacked ? (
          <>
            <Box className={classes.infoStacked}>
              <CoachInfo link={readyLink} />
            </Box>
            <Box className={classes.pickerStacked}>
              {slotTakenAlert}
              {picker}
            </Box>
          </>
        ) : (
          <>
            {slotNotice && (
              <Box p="lg" pb={0}>
                {slotTakenAlert}
              </Box>
            )}
            <div className={classes.split}>
              <Box className={classes.info}>
                <CoachInfo link={readyLink} />
              </Box>
              {picker}
            </div>
          </>
        )}
      </Paper>
    </PageShell>
  );
}

export default PublicBookingPage;
